import { useCallback } from 'react';
import { Checkbox } from '@scholastic/mockingjay';
import { cn } from '../../utils/cn/cn';
import type { GoogleCourse } from '../../hooks/api/use-get-google-courses-api/use-get-google-courses-api';

interface GoogleClassroomClassListProps {
  classes: GoogleCourse[];
  userName: string;
  selectedClassIds: string[];
  onSelectionChange: (selectedIds: string[]) => void;
  selectAllLabel?: string;
  className?: string;
  listClassName?: string;
}

function getSelectAllCheckedState(
  allSelected: boolean,
  someSelected: boolean
): boolean | 'indeterminate' {
  if (allSelected) {
    return true;
  }

  if (someSelected) {
    return 'indeterminate';
  }

  return false;
}

const CHECKBOX_LAYOUT_CLASSNAME =
  '[&>div]:w-full [&>div]:min-w-0 [&>div>div]:w-full [&>div>div]:min-w-0 [&>div>div]:items-start';

const CHECKBOX_CONTROL_CLASSNAME = '[&_button]:mt-[3px] [&_button]:shrink-0';

const CHECKBOX_LABEL_CLASSNAME =
  '[&_label]:min-w-0 [&_label]:flex-1 [&_label]:whitespace-normal [&_label]:break-words [&_label]:text-base [&_label]:font-medium [&_label]:leading-6 [&_label]:text-[#4D4D4D]';

const CHECKBOX_LABEL_WRAPPING_CLASSNAME = cn(
  CHECKBOX_LAYOUT_CLASSNAME,
  CHECKBOX_CONTROL_CLASSNAME,
  CHECKBOX_LABEL_CLASSNAME
);

function ClassCheckboxItem({
  gc,
  checked,
  indented,
  onToggle,
}: {
  gc: GoogleCourse;
  checked: boolean;
  indented: boolean;
  onToggle: (classId: string) => void;
}) {
  const handleChange = useCallback(() => {
    onToggle(gc.course_id);
  }, [gc.course_id, onToggle]);

  return (
    <div className={cn(indented && 'pl-6')}>
      <div className={CHECKBOX_LABEL_WRAPPING_CLASSNAME}>
        <Checkbox
          label={gc.course_name}
          checked={checked}
          onCheckedChange={handleChange}
        />
      </div>
    </div>
  );
}

export function GoogleClassroomClassList({
  classes,
  userName,
  selectedClassIds,
  onSelectionChange,
  selectAllLabel = 'Select all',
  className,
  listClassName,
}: GoogleClassroomClassListProps) {
  const someSelected = classes.some((c) =>
    selectedClassIds.includes(c.course_id)
  );
  const allSelected =
    classes.length > 0 &&
    classes.every((c) => selectedClassIds.includes(c.course_id));

  const handleSelectAll = useCallback(() => {
    if (allSelected) {
      onSelectionChange([]);
    } else {
      onSelectionChange(classes.map((c) => c.course_id));
    }
  }, [allSelected, classes, onSelectionChange]);

  const handleToggle = useCallback(
    (classId: string) => {
      if (selectedClassIds.includes(classId)) {
        onSelectionChange(selectedClassIds.filter((id) => id !== classId));
      } else {
        onSelectionChange([...selectedClassIds, classId]);
      }
    },
    [selectedClassIds, onSelectionChange]
  );

  return (
    <div
      className={cn(
        'flex min-h-0 w-full flex-col rounded border border-[#A9A9A9] bg-white px-4 py-3',
        className
      )}
    >
      <p className="text-sm font-bold leading-none text-[#191918]">
        {userName ? `${userName}'s Classes` : 'Your Classes'}
      </p>
      <div className={cn('mt-2 flex min-h-0 flex-1 flex-col', listClassName)}>
        <div className="min-h-6 shrink-0">
          <div className={CHECKBOX_LABEL_WRAPPING_CLASSNAME}>
            <Checkbox
              label={selectAllLabel}
              checked={getSelectAllCheckedState(allSelected, someSelected)}
              onCheckedChange={handleSelectAll}
            />
          </div>
        </div>
        <div className="mt-3 flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto">
          {classes.map((gc) => (
            <ClassCheckboxItem
              key={gc.course_id}
              gc={gc}
              checked={selectedClassIds.includes(gc.course_id)}
              indented
              onToggle={handleToggle}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
